import { useCallback, useEffect, useRef, useState } from "react";
import { createLogger } from "../lib/logger";
import { apiFetch } from "../services/api";

const slog = createLogger("shell-browser");

/**
 * Where the request actually runs.
 *
 * - `in_page`     — Chrome executes it in the loaded page (task 205). Default.
 * - `guest_proxy` — the backend replays the session cookies and the guest agent
 *                   performs the call from inside the microVM (task 206). Same
 *                   exit IP as the browser, but *not* the browser's TLS/HTTP
 *                   fingerprint.
 */
export type ShellRequestMode = "in_page" | "guest_proxy";

// ─── Public types ────────────────────────────────────────────────────

/** HTTP methods offered as static options in the shell-browser form. */
export type ShellMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type ShellHeader = { key: string; value: string };

export type ShellRequest = {
  /**
   * A plain string, not the five-value union: task 210 replays the method the
   * browser really used, which may be `HEAD`, `OPTIONS` or anything else.
   * Flattening it to `GET` would misreport what happened. Validation stays
   * where it belongs — the guest relay refuses methods outside its allowlist,
   * and `fetch` refuses malformed tokens.
   */
  method: string;
  url: string;
  headers: ShellHeader[];
  body: string;
  /**
   * Task 212 — internal to the frontend, never part of the backend contract.
   *
   * A body captured by task 210 is a *binary string*: one JS code unit per
   * original byte, as CDP delivers `postData`. A body typed by hand is text.
   * Treating every string as binary would break the implicit `Content-Type`
   * `fetch` derives from a string; treating every string as text re-encodes the
   * captured bytes in UTF-8 — the corruption this task fixes.
   *
   * `ShellRequestDto` carries `#[serde(deny_unknown_fields)]`, so this field is
   * stripped before the POST and never reaches the relay.
   */
  bodyKind?: "binary_string";
  /** Distinguishes a present zero-byte entity from an absent body. */
  bodyPresent?: boolean;
};

/**
 * Failure raised *inside the page*, by the browser itself.
 *
 * - `invalid_url`      — refused before any socket is opened (not http/https)
 * - `opaque`           — cross-origin opaque response: no status/headers/body
 * - `timeout`          — the in-page AbortController fired
 * - `cors_or_network`  — `fetch` rejected with a TypeError (CORS, DNS, TLS…)
 * - `page`             — any other exception raised by the evaluated code
 */
export type ShellPageErrorKind =
  | "invalid_url"
  | "opaque"
  | "timeout"
  | "cors_or_network"
  | "page";

export interface ShellPageError {
  kind: ShellPageErrorKind;
  message: string;
}

export interface ShellResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  /** Full body. Truncation is a display concern, never a data concern. */
  body: string;
  /** `Response.type` as seen by the page ("basic", "cors", …), or "guest-proxy". */
  responseType: string | null;
  /** User-visible duration: WebSocket open → result, in milliseconds. */
  durationMs: number;
  /** Guest mode only: "utf-8", or "base64" for a binary body. */
  bodyEncoding?: string;
  /** Guest mode only: how many cookies were replayed. A count, never a value. */
  cookiesApplied?: number;
  /** Guest mode only: the body was cut at the relay cap. */
  truncated?: boolean;
  sourceTabId?: string;
  sourceCaptureId?: number;
  nativeTransport?: string;
  fidelityLimits?: string[];
}

export interface UseShellRequestReturn {
  loading: boolean;
  response: ShellResponse | null;
  pageError: ShellPageError | null;
  transportError: string | null;
  /** Never rejects: every outcome is surfaced through the state above. */
  send: (request: ShellRequest, mode?: ShellRequestMode, tabId?: string,
    capture?: ShellCaptureBinding) => Promise<void>;
  snapshot?: ShellSnapshot | null;
  getSnapshot?: () => Promise<ShellSnapshot | null>;
  /** Task 210 — the last request the active tab actually sent, or null. */
  lastRequest: LastRequest | null;
  lastRequestStatus: LastRequestStatus;
  /** Never rejects. Reads it once, on explicit demand. */
  getLastRequest: () => Promise<LastRequest | null>;
  /** Task 213 — requests the tracker observed, newest first. */
  requestHistory: RequestHistorySummary[];
  requestHistoryStatus: LastRequestStatus;
  getRequestHistory: () => Promise<RequestHistorySummary[]>;
  getRequestHistoryEntry: (id: number) => Promise<RequestHistoryEntry | null>;
  /** Task 215 — the tabs currently open, read on demand. Never rejects. */
  openTabs: OpenTab[];
  openTabsStatus: LastRequestStatus;
  getOpenTabs: () => Promise<OpenTab[]>;
  /**
   * Task 219 — the URL and title of the tab Chrome really shows, read through
   * the same active-tab mechanism as `send()` (209). Never rejects: `null`
   * means the page could not be read. No state is kept in the hook — the
   * panel consumes the value once, at the click that asked for it.
   */
  getActivePage: () => Promise<ActivePage | null>;
  /**
   * Task 219 — the `Cookie` request header Chrome holds for this URL's host,
   * built only from cookies actually read back (`cmd_get_cookies`). `""` when
   * the read succeeded and found nothing, `null` when it failed or the URL is
   * not http(s). Never rejects, never invents a value.
   */
  getCookieHeader: (url: string) => Promise<string | null>;
}

/** Task 219 — what the active tab really shows, nothing more. */
export interface ActivePage {
  url: string;
  title: string;
  /** Actual evaluated CDP target; absent with older bridges. */
  tabId?: string;
}

/**
 * Task 219 — one cookie as `cmd_get_cookies` reports it. Only the fields the
 * header builder needs are kept; the value never reaches a log.
 */
export interface BrowserCookie {
  name: string;
  value: string;
  domain: string;
  secure: boolean;
  path?: string;
}

/**
 * One line of the history. Deliberately without headers or body: returning up
 * to 30 bodies of 1 MiB at once is exactly what the detail form exists to avoid.
 */
export interface RequestHistorySummary {
  id: number;
  /**
   * Task 214 — the CDP target that emitted this request, as published by
   * `list_tabs`. `id` identifies the history entry, `tabId` the tab: two
   * distinct notions, deliberately both present.
   */
  tabId: string;
  method: string;
  url: string;
  timestamp: number | null;
  revision?: number;
  resourceType?: string;
  epoch?: string;
}

/**
 * A full history entry. It is a `LastRequest` plus the tab identity, so the
 * panel can keep filling its form from either source without a second code
 * path — `LastRequest` and `getLastRequest()` stay untouched (210 contract).
 */
export interface RequestHistoryEntry extends LastRequest {
  tabId: string;
}

/**
 * Task 215 — one currently open tab, as published by the bridge's `list_tabs`.
 *
 * `tabId` is the same identity 214 puts on every history entry, which is what
 * lets the panel filter captures by tab without ever joining on the URL.
 */
export interface OpenTab {
  tabId: string;
  url: string;
  title: string;
  type: string;
  closed?: boolean;
  latestId?: number | null;
}

/**
 * The last request the visible tab really emitted — method, URL, every header
 * and the body when Chrome exposes it. Task 208 only ever synthesised
 * `GET <page URL>`, which is not what the browser sent.
 */
export interface LastRequest {
  method: string;
  url: string;
  headers: Record<string, string>;
  /** `""` when there is no body, `null` when Chrome could not provide it. */
  body: string | null;
  bodyTruncated: boolean;
  timestamp: number | null;
  capture?: ShellCaptureBinding;
  bodyBase64?: string | null;
  bodyState?: "absent" | "complete" | "pending" | "unavailable" | "truncated";
  headerProvenance?: string;
  headersState?: string;
  cookieProvenance?: string;
  resourceType?: string;
}

export interface ShellCaptureBinding {
  sessionId: string;
  epoch: string;
  tabId: string;
  id: number;
  revision: number;
  edited?: boolean;
}

export interface ShellSnapshot {
  epoch: string;
  browser: "chrome" | "firefox";
  activeTabId: string | null;
  tabs: OpenTab[];
  history: RequestHistorySummary[];
  overflow: boolean;
}

/**
 * Lifecycle of the read. `unavailable` is informative, never an error: a tab
 * that has not sent anything yet is not a failure of the request the user is
 * preparing.
 */
export type LastRequestStatus = "idle" | "loading" | "available" | "unavailable";

// ─── Constants ───────────────────────────────────────────────────────

/**
 * Timeout budget. The backend relay caps a CDP command at
 * `cdp_command_timeout_secs = 30`, and `CdpPool` serialises every command of a
 * session behind a single mutex — a shell request that outlived the relay would
 * keep the chat panel waiting. The page aborts first, the transport second, the
 * backend last, so the user always sees the most specific error.
 */
export const PAGE_TIMEOUT_MS = 20_000;
export const TRANSPORT_TIMEOUT_MS = 25_000;

/**
 * The read is a one-shot lookup, not a request the user is waiting on: it
 * gives up quickly rather than holding the panel's suggestion line.
 */
export const LAST_REQUEST_TIMEOUT_MS = 10_000;

/**
 * U+2028 / U+2029 are legal inside a JSON string but are JavaScript line
 * terminators. Built with `String.fromCharCode` on purpose: written literally
 * they are invisible in review, and inside a regex literal they are a syntax
 * error.
 */
const JS_LINE_SEPARATORS: ReadonlyArray<readonly [string, string]> = [
  [String.fromCharCode(0x2028), "\\u2028"],
  [String.fromCharCode(0x2029), "\\u2029"],
];

// ─── Pure helpers (exported for unit tests) ──────────────────────────

/**
 * JavaScript string literal for `payload`, safe to inline in evaluated code.
 *
 * `JSON.stringify` escapes quotes, backslashes and control characters; the two
 * line separators above are escaped on top of it. The evaluated program only
 * ever receives a string it `JSON.parse`s: the URL, the headers and the body
 * never reach an executable position.
 */
export function toJsStringLiteral(payload: string): string {
  let literal = JSON.stringify(payload);
  for (const [raw, escaped] of JS_LINE_SEPARATORS) {
    literal = literal.split(raw).join(escaped);
  }
  return literal;
}

/** Reject anything that is not an http(s) URL before opening a socket. */
export function isHttpUrl(raw: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return false;
  }
  return parsed.protocol === "http:" || parsed.protocol === "https:";
}

/** Collapse the key/value rows into a header map, dropping unnamed rows. */
export function toHeaderRecord(headers: ShellHeader[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const header of headers) {
    const key = header.key.trim();
    if (key) out[key] = header.value;
  }
  return out;
}

/**
 * Normalise a body into the one-character-one-byte string the relay DTO expects.
 *
 * A captured body already is one; a hand-typed body must be encoded to its real
 * UTF-8 bytes first, so that `é` or an emoji crosses the wire as the bytes the
 * browser would have sent — not as its code points.
 *
 * Converted in bounded blocks: `String.fromCharCode(...bytes)` on a 512 KiB
 * body would blow the call stack.
 */
export function toRelayBodyString(request: ShellRequest): string {
  if (request.bodyKind === "binary_string") return request.body;
  const bytes = new TextEncoder().encode(request.body);
  const BLOCK = 8192;
  let out = "";
  for (let start = 0; start < bytes.length; start += BLOCK) {
    out += String.fromCharCode(...bytes.subarray(start, start + BLOCK));
  }
  return out;
}

/**
 * The exact DTO the relay accepts. Built field by field on purpose:
 * `ShellRequestDto` is `deny_unknown_fields`, so leaking `bodyKind` would make
 * the backend reject the whole request.
 */
export function toRelayDto(request: ShellRequest): {
  method: string;
  url: string;
  headers: ShellHeader[];
  body: string;
} {
  return {
    method: request.method,
    url: request.url,
    headers: request.headers.filter(({ key, value }) => key.trim() !== "" || value !== ""),
    body: toRelayBodyString(request),
  };
}

/**
 * Build the `Runtime.evaluate` expression: an async IIFE running a real
 * `window.fetch()` in the page, with the session's cookies, User-Agent, TLS
 * stack and exit IP — by construction, not by imitation.
 */
export function buildFetchExpression(
  request: ShellRequest,
  timeoutMs: number = PAGE_TIMEOUT_MS,
): string {
  const payload = JSON.stringify({
    method: request.method,
    url: request.url,
    headers: toHeaderRecord(request.headers),
    body: request.body,
    bodyKind: request.bodyKind ?? null,
    bodyPresent: request.bodyPresent ?? request.body.length > 0,
    timeoutMs,
  });

  return `(async () => {
  const req = JSON.parse(${toJsStringLiteral(payload)});
  const controller = new AbortController();
  let timer = null;
  try {
    timer = setTimeout(function () { controller.abort(); }, req.timeoutMs);
    const init = {
      method: req.method,
      headers: req.headers,
      credentials: "include",
      signal: controller.signal
    };
    if (req.method !== "GET" && req.method !== "HEAD" && (req.body || req.bodyPresent)) {
      if (req.bodyKind === "binary_string") {
        // One code unit per original byte. Handing the string to fetch would
        // re-encode it as UTF-8; a Uint8Array is sent verbatim.
        const bytes = new Uint8Array(req.body.length);
        for (let i = 0; i < req.body.length; i++) {
          const code = req.body.charCodeAt(i);
          if (code > 255) {
            return { ok: false, error: { kind: "page", message: "Corps binaire invalide : un caractere hors de la plage 0-255 a l'index " + i + "." } };
          }
          bytes[i] = code & 0xff;
        }
        init.body = bytes;
      } else {
        init.body = req.body;
      }
    }
    const res = await fetch(req.url, init);
    if (res.type === "opaque") {
      return { ok: false, error: { kind: "opaque", message: "Reponse opaque : le navigateur n'expose ni statut, ni en-tetes, ni corps." } };
    }
    const body = await res.text();
    return {
      ok: true,
      status: res.status,
      statusText: res.statusText,
      headers: Object.fromEntries(res.headers.entries()),
      body: body,
      responseType: res.type
    };
  } catch (err) {
    if (err && err.name === "AbortError") {
      return { ok: false, error: { kind: "timeout", message: "Timeout page apres " + req.timeoutMs + " ms." } };
    }
    if (err instanceof TypeError) {
      return { ok: false, error: { kind: "cors_or_network", message: String((err && err.message) || err) } };
    }
    return { ok: false, error: { kind: "page", message: String((err && err.message) || err) } };
  } finally {
    if (timer !== null) { clearTimeout(timer); }
  }
})()`;
}

/** Outcome of one frame received on the CDP WebSocket. */
export type CdpOutcome =
  | { kind: "ignore" }
  | { kind: "transport"; message: string }
  | { kind: "page"; error: ShellPageError }
  | { kind: "ok"; response: Omit<ShellResponse, "durationMs"> };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asStringRecord(value: unknown): Record<string, string> {
  if (!isRecord(value)) return {};
  const out: Record<string, string> = {};
  for (const [key, raw] of Object.entries(value)) {
    if (typeof raw === "string") out[key] = raw;
  }
  return out;
}

const PAGE_ERROR_KINDS: readonly ShellPageErrorKind[] = [
  "invalid_url",
  "opaque",
  "timeout",
  "cors_or_network",
  "page",
] as const;

function asPageErrorKind(value: unknown): ShellPageErrorKind {
  return typeof value === "string" &&
    (PAGE_ERROR_KINDS as readonly string[]).includes(value)
    ? (value as ShellPageErrorKind)
    : "page";
}

/**
 * True when a node is the object the evaluated IIFE returns. Every branch of
 * that program yields a boolean `ok`, so this identifies the payload by its
 * contract rather than by its position in the frame.
 */
export function looksLikeShellPayload(value: unknown): value is Record<string, unknown> {
  return isRecord(value) && typeof value.ok === "boolean";
}

/**
 * Locate the evaluated value inside `frame.result`.
 *
 * Several guest bridge variants coexist in production and disagree on where
 * they put it:
 *
 * - port 9222 — `{result: <valeur>, type: "<type>"}`: the bridge already
 *   unwrapped the CDP RemoteObject. This is also the shape the production chat
 *   consumes (`routes/chat.rs` reads `resp.result["result"]` directly).
 * - port 8500 (`binaries/cdb-bridge`) — `{result: {type, value}}`: the raw CDP
 *   RemoteObject is forwarded verbatim, so the payload sits under `.value`.
 *
 * Reading one fixed path made the panel fail on every call against the other
 * variant (cycle 1 defect). Candidates are therefore probed by contract, so a
 * future variant that moves the payload again is still handled.
 */
export function extractEvaluateValue(result: Record<string, unknown>): Record<string, unknown> | null {
  const inner = result.result;
  const candidates: unknown[] = [
    inner,
    isRecord(inner) ? inner.value : undefined,
    result.value,
    result,
  ];
  for (const candidate of candidates) {
    if (looksLikeShellPayload(candidate)) return candidate;
  }
  return null;
}

/**
 * Classify one relay frame. A malformed frame is a protocol failure, never an
 * empty HTTP response — the two must not be confused in the UI.
 *
 * `status: "error"` is honoured whatever the `request_id`: the relay answers an
 * unparseable command with `request_id: ""` (`routes/cdp_relay.rs`), and each
 * shell request owns its own short-lived socket, so such a frame is ours.
 * Success frames carry the bridge status verbatim ("ok"), so anything that is
 * not an explicit error is parsed as a result.
 */
export function interpretCdpMessage(raw: unknown, requestId: string): CdpOutcome {
  let frame: unknown;
  if (typeof raw === "string") {
    try {
      frame = JSON.parse(raw);
    } catch {
      return { kind: "transport", message: "Transport CDP : réponse illisible (JSON invalide)." };
    }
  } else {
    frame = raw;
  }

  if (!isRecord(frame)) {
    return { kind: "transport", message: "Transport CDP : réponse mal formée." };
  }

  if (frame.status === "error" || typeof frame.error === "string") {
    const detail = typeof frame.error === "string" ? frame.error : "erreur relais";
    return { kind: "transport", message: `Transport CDP : ${detail}` };
  }

  if (
    typeof frame.request_id === "string" &&
    frame.request_id !== "" &&
    frame.request_id !== requestId
  ) {
    return { kind: "ignore" };
  }

  const result = frame.result;
  if (!isRecord(result)) {
    return { kind: "transport", message: "Transport CDP : réponse sans résultat exploitable." };
  }

  // An uncaught exception in the evaluated program originates in the page, not
  // in the transport: the IIFE catches everything, so this is a last-resort path.
  if (isRecord(result.exceptionDetails)) {
    const text = result.exceptionDetails.text;
    return {
      kind: "page",
      error: {
        kind: "page",
        message: typeof text === "string" ? text : "Exception non capturée dans la page.",
      },
    };
  }

  const value = extractEvaluateValue(result);
  if (!value) {
    // Naming the keys actually received turns "mal formée" into a diagnosis:
    // a bridge variant that moved the payload is identified on the first call.
    return {
      kind: "transport",
      message: `Transport CDP : valeur Runtime.evaluate introuvable (clés result: ${Object.keys(result).join(", ") || "aucune"}).`,
    };
  }

  if (value.ok === false) {
    const error = isRecord(value.error) ? value.error : {};
    return {
      kind: "page",
      error: {
        kind: asPageErrorKind(error.kind),
        message:
          typeof error.message === "string"
            ? error.message
            : "Échec de la requête dans la page.",
      },
    };
  }

  if (value.ok === true && typeof value.status === "number" && typeof value.body === "string") {
    return {
      kind: "ok",
      response: {
        status: value.status,
        statusText: typeof value.statusText === "string" ? value.statusText : "",
        headers: asStringRecord(value.headers),
        body: value.body,
        responseType: typeof value.responseType === "string" ? value.responseType : null,
        nativeTransport: typeof value.transport === "string" ? value.transport : undefined,
        sourceTabId: typeof value.tab_id === "string" ? value.tab_id : undefined,
        sourceCaptureId: typeof value.capture_id === "number" ? value.capture_id : undefined,
        fidelityLimits: Array.isArray(value.fidelity_limits)
          ? value.fidelity_limits.filter((item): item is string => typeof item === "string").slice(0,16) : undefined,
      },
    };
  }

  return { kind: "transport", message: "Transport CDP : valeur Runtime.evaluate mal formée." };
}

/**
 * Map the backend DTO of the guest relay onto the shared `ShellResponse`.
 *
 * Validated by contract, like every other payload here: a malformed answer is a
 * protocol failure, never an empty HTTP response.
 */
export function parseGuestResponse(payload: unknown, durationMs: number): ShellResponse | null {
  if (!isRecord(payload)) return null;
  if (typeof payload.status !== "number" || typeof payload.body !== "string") return null;
  return {
    status: payload.status,
    statusText: typeof payload.status_text === "string" ? payload.status_text : "",
    headers: asStringRecord(payload.headers),
    body: payload.body,
    responseType:
      typeof payload.response_type === "string" ? payload.response_type : "guest-proxy",
    durationMs:
      typeof payload.duration_ms === "number" ? payload.duration_ms : durationMs,
    bodyEncoding: typeof payload.body_encoding === "string" ? payload.body_encoding : "utf-8",
    cookiesApplied:
      typeof payload.cookies_applied === "number" ? payload.cookies_applied : undefined,
    truncated: payload.truncated === true,
  };
}

/** Extract the backend's stable error code without inventing one. */
export function guestErrorMessage(status: number, payload: unknown): string {
  if (isRecord(payload)) {
    const code = typeof payload.code === "string" ? payload.code : null;
    const detail = typeof payload.error === "string" ? payload.error : null;
    if (code && detail) return `${code} — ${detail}`;
    if (code) return code;
    if (detail) return detail;
  }
  return `relais VM : réponse HTTP ${status}`;
}

/** Outcome of one frame received while reading the last request. */
export type LastRequestOutcome =
  | { kind: "ignore" }
  | { kind: "unavailable" }
  | { kind: "ok"; request: LastRequest };

/**
 * Classify one relay frame for the last-request read.
 *
 * The payload is the bridge's own contract (`{captured, request}`), not a
 * `Runtime.evaluate` result: `extractEvaluateValue` has no business here.
 * Anything that is not a complete, well-formed capture — `captured:false`
 * included — is `unavailable`, a neutral state and never an error.
 */
export function interpretLastRequestMessage(
  raw: unknown,
  requestId: string,
): LastRequestOutcome {
  let frame: unknown;
  if (typeof raw === "string") {
    try {
      frame = JSON.parse(raw);
    } catch {
      return { kind: "unavailable" };
    }
  } else {
    frame = raw;
  }
  if (!isRecord(frame)) return { kind: "unavailable" };
  if (frame.status === "error" || typeof frame.error === "string") {
    return { kind: "unavailable" };
  }
  if (
    typeof frame.request_id === "string" &&
    frame.request_id !== "" &&
    frame.request_id !== requestId
  ) {
    return { kind: "ignore" };
  }
  const result = frame.result;
  if (!isRecord(result)) return { kind: "unavailable" };
  if (result.captured !== true) return { kind: "unavailable" };
  const request = result.request;
  if (!isRecord(request)) return { kind: "unavailable" };
  if (typeof request.method !== "string" || typeof request.url !== "string") {
    return { kind: "unavailable" };
  }
  const body =
    typeof request.body === "string" ? request.body : request.body === null ? null : "";
  return {
    kind: "ok",
    request: {
      method: request.method,
      url: request.url,
      headers: asStringRecord(request.headers),
      body,
      bodyTruncated: request.body_truncated === true,
      timestamp: typeof request.timestamp === "number" ? request.timestamp : null,
    },
  };
}

/**
 * The CDP relay URL. Single builder so the page read and the shell request can
 * never drift apart. Never logged: it carries the session token.
 */
export function buildCdpWebSocketUrl(sessionId: string, sessionToken: string): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/api/v1/sessions/${sessionId}/cdp?token=${encodeURIComponent(sessionToken)}`;
}

/** Parse the list form of `get_request_history`. */
export function interpretHistoryList(
  raw: unknown,
  requestId: string,
): { kind: "ignore" } | { kind: "unavailable" } | { kind: "ok"; history: RequestHistorySummary[] } {
  const frame = parseRelayFrame(raw, requestId);
  if (frame.kind !== "result") return frame.kind === "ignore" ? { kind: "ignore" } : { kind: "unavailable" };
  const list = frame.result.history;
  if (!Array.isArray(list)) return { kind: "unavailable" };
  const history: RequestHistorySummary[] = [];
  for (const item of list) {
    if (!isRecord(item)) return { kind: "unavailable" };
    if (typeof item.id !== "number" || typeof item.method !== "string" || typeof item.url !== "string") {
      return { kind: "unavailable" };
    }
    // Task 214: an entry without a usable tab identity is rejected outright.
    // Defaulting to "" would silently produce a bucket that groups unrelated
    // tabs together — worse than showing nothing.
    if (typeof item.tab_id !== "string" || item.tab_id === "") {
      return { kind: "unavailable" };
    }
    history.push({
      id: item.id,
      tabId: item.tab_id,
      method: item.method,
      url: item.url,
      timestamp: typeof item.timestamp === "number" ? item.timestamp : null,
    });
  }
  // An empty history is a valid answer, not a failure.
  return { kind: "ok", history };
}

/** Parse the detail form. An evicted id answers `found:false`, not an error. */
export function interpretHistoryEntry(
  raw: unknown,
  requestId: string,
): { kind: "ignore" } | { kind: "unavailable" } | { kind: "ok"; request: RequestHistoryEntry } {
  const frame = parseRelayFrame(raw, requestId);
  if (frame.kind !== "result") return frame.kind === "ignore" ? { kind: "ignore" } : { kind: "unavailable" };
  if (frame.result.found !== true) return { kind: "unavailable" };
  const request = frame.result.request;
  if (!isRecord(request)) return { kind: "unavailable" };
  if (typeof request.method !== "string" || typeof request.url !== "string") {
    return { kind: "unavailable" };
  }
  // Same rule as the list: the detail is what 215 will group by.
  if (typeof request.tab_id !== "string" || request.tab_id === "") {
    return { kind: "unavailable" };
  }
  const body =
    typeof request.body === "string" ? request.body : request.body === null ? null : "";
  return {
    kind: "ok",
    request: {
      tabId: request.tab_id,
      method: request.method,
      url: request.url,
      headers: asStringRecord(request.headers),
      body,
      bodyTruncated: request.body_truncated === true,
      timestamp: typeof request.timestamp === "number" ? request.timestamp : null,
    },
  };
}

/**
 * Parse `list_tabs` (task 215).
 *
 * Only `page` targets are kept: a service worker or an extension background
 * page is a CDP target too, and offering one as a browsable tab would be a
 * dead end for the user.
 *
 * `active` is deliberately ignored — the bridge publishes it as `false` for
 * every tab, and the panel's selected tab is a local filter, not Chrome focus.
 */
export function interpretOpenTabs(
  raw: unknown,
  requestId: string,
): { kind: "ignore" } | { kind: "unavailable" } | { kind: "ok"; tabs: OpenTab[] } {
  const frame = parseRelayFrame(raw, requestId);
  if (frame.kind !== "result") return frame.kind === "ignore" ? { kind: "ignore" } : { kind: "unavailable" };
  const list = frame.result.tabs;
  if (!Array.isArray(list)) return { kind: "unavailable" };
  const tabs: OpenTab[] = [];
  for (const item of list) {
    if (!isRecord(item)) return { kind: "unavailable" };
    if (typeof item.tab_id !== "string" || item.tab_id === "") return { kind: "unavailable" };
    if (typeof item.url !== "string") return { kind: "unavailable" };
    const type = typeof item.type === "string" ? item.type : "";
    if (type !== "page") continue;
    tabs.push({
      tabId: item.tab_id,
      url: item.url,
      title: typeof item.title === "string" ? item.title : "",
      type,
    });
  }
  // No open tab is a valid answer, not a failure.
  return { kind: "ok", tabs };
}

/**
 * Task 219 — the expression evaluated on the active tab by `getActivePage`.
 *
 * Side-effect free by construction: two reads, no assignment, no DOM mutation,
 * no navigation. Wrapped in a function call so the object literal is an
 * expression and never parsed as a block. `String()` keeps the contract strict
 * even on a page that overrode `document.title` with something exotic.
 */
export const ACTIVE_PAGE_EXPRESSION =
  "(function () { return { url: String(window.location.href), title: String(document.title) }; })()";

/** True when a node is the object `ACTIVE_PAGE_EXPRESSION` returns. */
export function isActivePagePayload(value: unknown): value is ActivePage {
  return isRecord(value) && typeof value.url === "string" && typeof value.title === "string";
}

/**
 * Parse the `Runtime.evaluate` answer of `cdp_evaluate_active_tab` for the
 * active page. The payload is probed at the same positions as
 * `extractEvaluateValue` — both deployed bridge variants are covered — but by
 * its own contract (`{url, title}`), never by the shell `ok` flag.
 */
export function interpretActivePage(
  raw: unknown,
  requestId: string,
): { kind: "ignore" } | { kind: "unavailable" } | { kind: "ok"; page: ActivePage } {
  const frame = parseRelayFrame(raw, requestId);
  if (frame.kind !== "result") return frame.kind === "ignore" ? { kind: "ignore" } : { kind: "unavailable" };
  const result = frame.result;
  // An exception in the page is a failed read, never a page with an empty URL.
  if (isRecord(result.exceptionDetails)) return { kind: "unavailable" };
  const inner = result.result;
  const candidates: unknown[] = [
    inner,
    isRecord(inner) ? inner.value : undefined,
    result.value,
    result,
  ];
  for (const candidate of candidates) {
    if (isActivePagePayload(candidate)) {
      return {
        kind: "ok",
        page: {
          url: candidate.url,
          title: candidate.title,
          // Only bridge metadata is authoritative; never trust page JS to
          // supply its own CDP identity in the evaluated value.
          ...(typeof result.tab_id === "string" && result.tab_id !== ""
            ? { tabId: result.tab_id }
            : {}),
        },
      };
    }
  }
  return { kind: "unavailable" };
}

/**
 * Parse `get_cookies`. A pair is kept only when `name` is a non-empty string
 * and `value` a string: anything else is skipped rather than turned into a
 * plausible `name=` — nothing the browser does not hold may reach the header.
 * A `cookies` field that is not an array is a failed read, never "no cookie".
 */
export function interpretCookies(
  raw: unknown,
  requestId: string,
): { kind: "ignore" } | { kind: "unavailable" } | { kind: "ok"; cookies: BrowserCookie[] } {
  const frame = parseRelayFrame(raw, requestId);
  if (frame.kind !== "result") return frame.kind === "ignore" ? { kind: "ignore" } : { kind: "unavailable" };
  const list = frame.result.cookies;
  if (!Array.isArray(list)) return { kind: "unavailable" };
  const cookies: BrowserCookie[] = [];
  for (const item of list) {
    if (!isRecord(item)) continue;
    if (typeof item.name !== "string" || item.name === "" || typeof item.value !== "string") continue;
    cookies.push({
      name: item.name,
      value: item.value,
      domain: typeof item.domain === "string" ? item.domain : "",
      secure: item.secure === true,
      ...(typeof item.path === "string" ? { path: item.path } : {}),
    });
  }
  return { kind: "ok", cookies };
}

/**
 * Cookie domain-match, the loose form: a cookie for `shop.test` or
 * `.shop.test` matches `shop.test` and `www.shop.test`. The bridge already
 * scoped the read to the host (`Network.getCookies` with the host's URLs); this
 * is a safety net against a variant that returns more than asked, so that a
 * cookie of another site never becomes a line of the form. Host-only cookies
 * are treated like domain cookies on purpose — CDP has already decided
 * applicability, and a false negative here would silently drop a real cookie.
 */
export function cookieDomainMatches(cookieDomain: string, hostname: string): boolean {
  const domain = cookieDomain.trim().toLowerCase().replace(/^\./, "");
  const host = hostname.trim().toLowerCase().replace(/^\[|\]$/g, "");
  if (!domain || !host) return false;
  return host === domain || host.endsWith("." + domain);
}

/**
 * The `Cookie` header for `pageUrl`, from cookies actually read. Only pairs
 * whose domain matches the host are joined, and on a plain `http:` page the
 * `Secure` ones are left out — Chrome would not send them either. `""` when
 * nothing applies; the caller decides that an empty header is no header.
 */
export function buildCookieHeader(cookies: BrowserCookie[], pageUrl: string): string {
  let parsed: URL;
  try {
    parsed = new URL(pageUrl);
  } catch {
    return "";
  }
  const secureContext = parsed.protocol === "https:";
  if (!secureContext && parsed.protocol !== "http:") return "";
  return cookies
    .filter((cookie) => cookieDomainMatches(cookie.domain, parsed.hostname))
    .filter((cookie) => secureContext || !cookie.secure)
    .filter((cookie) => {
      const path = cookie.path || "/";
      return parsed.pathname === path || (
        parsed.pathname.startsWith(path) &&
        (path.endsWith("/") || parsed.pathname.charAt(path.length) === "/")
      );
    })
    .map((cookie) => cookie.name + "=" + cookie.value)
    .join("; ");
}

/** Shared frame validation: correlation, explicit error, result shape. */
function parseRelayFrame(
  raw: unknown,
  requestId: string,
): { kind: "ignore" } | { kind: "invalid" } | { kind: "result"; result: Record<string, unknown> } {
  let frame: unknown;
  if (typeof raw === "string") {
    try {
      frame = JSON.parse(raw);
    } catch {
      return { kind: "invalid" };
    }
  } else {
    frame = raw;
  }
  if (!isRecord(frame)) return { kind: "invalid" };
  if (frame.status === "error" || typeof frame.error === "string") return { kind: "invalid" };
  if (
    typeof frame.request_id === "string" &&
    frame.request_id !== "" &&
    frame.request_id !== requestId
  ) {
    return { kind: "ignore" };
  }
  const result = frame.result;
  if (!isRecord(result)) return { kind: "invalid" };
  return { kind: "result", result };
}

/**
 * Task 213 — one guarded, single-shot bridge read.
 *
 * The list and the detail need the same socket dance as 210 but must not share
 * its guards: a detail read must never cancel a list refresh, and neither may
 * touch `send()`. Rather than copy the dance twice more, it lives here once and
 * receives the refs it must own. `getLastRequest` keeps its own inlined copy —
 * rewriting a shipped path to prove a refactor is how regressions get in.
 */
function runBridgeRead<T>(config: {
  wsUrl: string;
  requestId: string;
  tool: string;
  args: Record<string, unknown>;
  timeoutMs: number;
  label: string;
  sessionId: string;
  interpret: (
    raw: unknown,
    requestId: string,
  ) => { kind: "ignore" } | { kind: "unavailable" } | { kind: "ok"; value: T };
  guardRef: { current: Promise<T | null> | null };
  cleanupRef: { current: (() => void) | null };
  onSettle: (value: T | null) => void;
}): Promise<T | null> {
  const { requestId, sessionId, label, guardRef, cleanupRef } = config;

  // Guard armed before any work can settle — the 208 lesson: `new Promise` runs
  // its executor synchronously, so a `new WebSocket` that throws would settle
  // before an assignment placed after it, installing a resolved promise that
  // nothing ever clears and freezing every later read.
  let settleAttempt!: (value: T | null) => void;
  const attempt = new Promise<T | null>((resolve) => {
    settleAttempt = resolve;
  });
  guardRef.current = attempt;

  let settled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let socket: WebSocket | null = null;

  const teardown = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      try {
        socket.close();
      } catch {
        // socket already closed by the browser
      }
      socket = null;
    }
    // Release refs only while they are still ours: a superseded read must never
    // tear down the one that replaced it.
    if (cleanupRef.current === ownCleanup) cleanupRef.current = null;
    if (guardRef.current === attempt) guardRef.current = null;
  };

  const settle = (value: T | null) => {
    if (settled) return;
    settled = true;
    teardown();
    config.onSettle(value);
    settleAttempt(value);
  };

  // Unmount: release everything without touching React state.
  const ownCleanup = () => {
    if (settled) return;
    settled = true;
    teardown();
    settleAttempt(null);
  };
  cleanupRef.current = ownCleanup;

  try {
    socket = new WebSocket(config.wsUrl);
  } catch {
    slog.warn(label + ": websocket unavailable", { sessionId, requestId });
    settle(null);
    return attempt;
  }

  timer = setTimeout(() => {
    slog.warn(label + ": timeout", { sessionId, requestId });
    settle(null);
  }, config.timeoutMs);

  socket.onopen = () => {
    socket?.send(
      JSON.stringify({
        action: "cdp",
        request_id: requestId,
        tool: config.tool,
        args: config.args,
        ts: new Date().toISOString(),
      }),
    );
  };

  socket.onmessage = (event: MessageEvent) => {
    const outcome = config.interpret(event.data, requestId);
    if (outcome.kind === "ignore") return;
    settle(outcome.kind === "ok" ? outcome.value : null);
  };

  socket.onerror = () => {
    slog.warn(label + ": socket error", { sessionId, requestId });
    settle(null);
  };

  socket.onclose = () => {
    // A close before any usable frame is a failure, not an empty answer.
    settle(null);
  };

  return attempt;
}

// ─── Hook ────────────────────────────────────────────────────────────

/**
 * Run one HTTP request inside the session's Chrome, through the existing CDP
 * relay. No backend route is added: the request is a real `window.fetch()`
 * evaluated in the loaded page.
 */
export function useShellRequest(
  sessionId: string,
  sessionToken: string,
): UseShellRequestReturn {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<ShellResponse | null>(null);
  const [pageError, setPageError] = useState<ShellPageError | null>(null);
  const [transportError, setTransportError] = useState<string | null>(null);

  // Teardown of the in-flight attempt (timer + handlers + socket), kept in a
  // ref so unmount releases it too.
  const cleanupRef = useRef<(() => void) | null>(null);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  // Task 208 — the page read owns its own state, socket, timer and guards.
  // Sharing any of them with `send()` would let one operation cancel the other
  // or leave an orphan socket behind.
  const [lastRequest, setLastRequest] = useState<LastRequest | null>(null);
  const [lastRequestStatus, setLastRequestStatus] = useState<LastRequestStatus>("idle");
  const lastRequestCleanupRef = useRef<(() => void) | null>(null);
  const lastRequestInFlightRef = useRef<Promise<LastRequest | null> | null>(null);

  // Task 213 — the history list and the entry detail are two more independent
  // operations. Distinct refs, so a detail read cannot cancel a list refresh.
  const [requestHistory, setRequestHistory] = useState<RequestHistorySummary[]>([]);
  const [requestHistoryStatus, setRequestHistoryStatus] = useState<LastRequestStatus>("idle");
  const historyCleanupRef = useRef<(() => void) | null>(null);
  const historyInFlightRef = useRef<Promise<RequestHistorySummary[] | null> | null>(null);
  const entryCleanupRef = useRef<(() => void) | null>(null);
  const entryInFlightRef = useRef<Promise<RequestHistoryEntry | null> | null>(null);
  // Keyed by id: a second click on the SAME entry reuses the read; selecting a
  // different entry supersedes it instead of racing two sockets to the form.
  const entryInFlightIdRef = useRef<number | null>(null);

  // Task 215 — the open tabs are a fourth independent read: their own guard and
  // cleanup, so refreshing them can never cancel a history or last-request read.
  const [openTabs, setOpenTabs] = useState<OpenTab[]>([]);
  const [openTabsStatus, setOpenTabsStatus] = useState<LastRequestStatus>("idle");
  const tabsCleanupRef = useRef<(() => void) | null>(null);
  const tabsInFlightRef = useRef<Promise<OpenTab[] | null> | null>(null);

  // Task 219 — two more independent reads, each with its own guard and
  // cleanup: the active page and the cookies of one host. Neither may cancel
  // a send, a history read or a tab read, and neither keeps React state — the
  // panel owns what it does with the values.
  const activePageCleanupRef = useRef<(() => void) | null>(null);
  const activePageInFlightRef = useRef<Promise<ActivePage | null> | null>(null);
  const cookiesCleanupRef = useRef<(() => void) | null>(null);
  const cookiesInFlightRef = useRef<Promise<string | null> | null>(null);
  // Same URL reuses the read; another path/scheme supersedes it. Cookies and
  // the resulting header are URL-dependent, even on the same host.
  const cookiesInFlightUrlRef = useRef<string | null>(null);
  const [snapshot, setSnapshot] = useState<ShellSnapshot | null>(null);
  const snapshotRef = useRef<ShellSnapshot | null>(null);
  const shellReadsRef = useRef(new Set<{ current: (() => void) | null }>());
  const snapshotPromiseRef = useRef<Promise<ShellSnapshot | null> | null>(null);
  const scopeRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    scopeRef.current += 1;
    snapshotRef.current = null;
    setSnapshot(null);
    setOpenTabs([]);
    setRequestHistory([]);
    setLastRequest(null);
    setResponse(null);
    return () => {
      mountedRef.current = false;
      cleanupRef.current?.();
      cleanupRef.current = null;
      // Idempotent on purpose: React StrictMode replays effect/cleanup in
      // development, so a first attempt may be cancelled before it settles.
      lastRequestCleanupRef.current?.();
      lastRequestCleanupRef.current = null;
      historyCleanupRef.current?.();
      historyCleanupRef.current = null;
      entryCleanupRef.current?.();
      entryCleanupRef.current = null;
      tabsCleanupRef.current?.();
      tabsCleanupRef.current = null;
      activePageCleanupRef.current?.();
      activePageCleanupRef.current = null;
      cookiesCleanupRef.current?.();
      cookiesCleanupRef.current = null;
      for (const cleanup of shellReadsRef.current) cleanup.current?.();
      shellReadsRef.current.clear();
      snapshotPromiseRef.current = null;
    };
  }, [sessionId, sessionToken]);

  const callShell = useCallback(async (tool: string, args: Record<string, unknown>) => {
    if (!sessionId || !sessionToken) return null;
    const cleanup = { current: null as (() => void) | null };
    shellReadsRef.current.add(cleanup);
    const scope = scopeRef.current;
    const value = await runBridgeRead<Record<string, unknown>>({
      wsUrl: buildCdpWebSocketUrl(sessionId, sessionToken), requestId: crypto.randomUUID(),
      tool, args, timeoutMs: LAST_REQUEST_TIMEOUT_MS, label: "shell capture read", sessionId,
      guardRef: { current: null }, cleanupRef: cleanup, onSettle: () => {},
      interpret: (raw, id) => {
        const frame = parseRelayFrame(raw, id);
        if (frame.kind === "ignore") return frame;
        return frame.kind === "result" ? {kind: "ok", value: frame.result} : {kind: "unavailable"};
      },
    });
    shellReadsRef.current.delete(cleanup);
    return mountedRef.current && scope === scopeRef.current ? value : null;
  }, [sessionId, sessionToken]);

  const getSnapshot = useCallback((): Promise<ShellSnapshot | null> => {
    if (snapshotPromiseRef.current) return snapshotPromiseRef.current;
    const promise = callShell("cdp_shell_snapshot", {}).then(value => {
      if (!value || typeof value.epoch !== "string" || !Array.isArray(value.tabs) ||
          !Array.isArray(value.history) || !["chrome", "firefox"].includes(String(value.browser))) {
        if (mountedRef.current) {setOpenTabsStatus("unavailable"); setRequestHistoryStatus("unavailable");}
        return null;
      }
      const tabs: OpenTab[] = value.tabs.filter(isRecord).filter(t => typeof t.tab_id === "string")
        .map(t => ({tabId: String(t.tab_id), url: String(t.url ?? ""), title: String(t.title ?? ""),
          type: "page", closed: t.closed === true, latestId: typeof t.latest_id === "number" ? t.latest_id : null}));
      const history: RequestHistorySummary[] = value.history.filter(isRecord)
        .filter(e => typeof e.id === "number" && typeof e.tab_id === "string")
        .map(e => ({id: Number(e.id), tabId: String(e.tab_id), method: String(e.method), url: String(e.url),
          timestamp: typeof e.timestamp === "number" ? e.timestamp : null, revision: Number(e.revision ?? 1),
          resourceType: String(e.resource_type ?? "Unknown"), epoch: String(value.epoch)}));
      const next: ShellSnapshot = {epoch: value.epoch, browser: value.browser as "chrome" | "firefox",
        activeTabId: typeof value.active_tab_id === "string" ? value.active_tab_id : null,
        tabs, history, overflow: value.overflow === true};
      snapshotRef.current = next;
      setSnapshot(next); setOpenTabs(tabs); setRequestHistory(history);
      setOpenTabsStatus("available"); setRequestHistoryStatus("available");
      return next;
    }).finally(() => {if (snapshotPromiseRef.current === promise) snapshotPromiseRef.current = null;});
    snapshotPromiseRef.current = promise;
    return promise;
  }, [callShell]);

  const getBoundCapture = useCallback(async (id: number): Promise<RequestHistoryEntry | null> => {
    const current = snapshotRef.current;
    const summary = current?.history.find(row => row.id === id);
    if (!current || !summary) return null;
    const result = await callShell("cdp_shell_capture", {epoch:current.epoch,tab_id:summary.tabId,id});
    if (!result || !isRecord(result.request)) return null;
    const row = result.request;
    if (row.epoch !== current.epoch || row.tab_id !== summary.tabId || row.history_id !== id ||
        typeof row.method !== "string" || typeof row.url !== "string") return null;
    const states = ["absent", "complete", "pending", "unavailable", "truncated"];
    if (!states.includes(String(row.body_state))) return null;
    let body: string | null = null;
    if (row.body_state === "absent") body = "";
    else if (row.body_state === "complete" && typeof row.body_base64 === "string") {
      try {body = atob(row.body_base64);} catch {return null;}
    }
    const entry: RequestHistoryEntry = {tabId:summary.tabId,method:row.method,url:row.url,
      headers:asStringRecord(row.headers),body,bodyTruncated:row.body_state === "truncated",
      timestamp:typeof row.timestamp === "number" ? row.timestamp : null,
      bodyBase64:typeof row.body_base64 === "string" ? row.body_base64 : null,
      bodyState:row.body_state as LastRequest["bodyState"],headersState:String(row.headers_state),
      headerProvenance:String(row.header_provenance),cookieProvenance:String(row.cookie_provenance),
      resourceType:String(row.resource_type),
      capture:{sessionId,epoch:current.epoch,tabId:summary.tabId,id,revision:Number(row.revision ?? 1)}};
    return entry;
  }, [callShell, sessionId]);

  /**
   * Read the loaded page once. Never rejects, never polls, never touches the
   * shell request state: a failure here leaves an already displayed response
   * exactly as it was.
   */
  const getLastRequest = useCallback((): Promise<LastRequest | null> => {
    // A second click while a read is in flight reuses the same promise instead
    // of opening a second socket.
    const running = lastRequestInFlightRef.current;
    if (running) return running;

    if (!sessionId || !sessionToken) {
      if (mountedRef.current) {
        setLastRequest(null);
        setLastRequestStatus("unavailable");
      }
      return Promise.resolve(null);
    }

    if (mountedRef.current) {
      // Drop the previous value up front: proposing a URL the browser has
      // already left would be worse than proposing nothing.
      setLastRequest(null);
      setLastRequestStatus("loading");
    }

    const requestId = crypto.randomUUID();
    const wsUrl = buildCdpWebSocketUrl(sessionId, sessionToken);
    slog.info("last request: reading", { sessionId, requestId });

    // The in-flight guard is armed BEFORE any work can settle.
    //
    // `new Promise` runs its executor synchronously: with the assignment placed
    // after it, a `new WebSocket` that throws would tear down a ref that is not
    // set yet — a no-op — and the assignment would then install an
    // already-resolved promise that nothing ever clears. Every later refresh
    // would return it and freeze, silently. `send()` arms its own guard before
    // its promise for exactly this reason; this now follows the same order.
    let settleAttempt!: (request: LastRequest | null) => void;
    const attempt = new Promise<LastRequest | null>((resolve) => {
      settleAttempt = resolve;
    });
    lastRequestInFlightRef.current = attempt;

    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let socket: WebSocket | null = null;

    const teardown = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        try {
          socket.close();
        } catch {
          // socket already closed by the browser
        }
        socket = null;
      }
      lastRequestCleanupRef.current = null;
      // Only release the guard if it is still ours: a later attempt must never
      // be cancelled by the teardown of an earlier one.
      if (lastRequestInFlightRef.current === attempt) {
        lastRequestInFlightRef.current = null;
      }
    };

    const settle = (request: LastRequest | null) => {
      if (settled) return;
      settled = true;
      teardown();
      if (mountedRef.current) {
        setLastRequest(request);
        setLastRequestStatus(request ? "available" : "unavailable");
      }
      settleAttempt(request);
    };

    // Unmount: release everything without touching React state.
    lastRequestCleanupRef.current = () => {
      if (settled) return;
      settled = true;
      teardown();
      settleAttempt(null);
    };

    try {
      socket = new WebSocket(wsUrl);
    } catch {
      slog.warn("last request: websocket unavailable", { sessionId, requestId });
      settle(null);
      return attempt;
    }

    timer = setTimeout(() => {
      slog.warn("last request: timeout", { sessionId, requestId });
      settle(null);
    }, LAST_REQUEST_TIMEOUT_MS);

    socket.onopen = () => {
      socket?.send(
        JSON.stringify({
          action: "cdp",
          request_id: requestId,
          // Task 210: the bridge's own contract, not a Runtime.evaluate.
          tool: "cdp_get_last_request",
          args: {},
          ts: new Date().toISOString(),
        }),
      );
    };

    socket.onmessage = (event: MessageEvent) => {
      const outcome = interpretLastRequestMessage(event.data, requestId);
      if (outcome.kind === "ignore") return;
      // Neither URL, headers nor body is ever logged: a captured request is
      // made of exactly the things that must not reach a log.
      slog.info("last request: read", { sessionId, requestId, kind: outcome.kind });
      settle(outcome.kind === "ok" ? outcome.request : null);
    };

    socket.onerror = () => settle(null);
    socket.onclose = () => settle(null);

    return attempt;
  }, [sessionId, sessionToken]);

  /**
   * Task 213 — read the bounded history once, on demand. Never rejects, never
   * polls. An empty history is a valid answer: `available` with zero entries,
   * not `unavailable` — nothing captured yet is not a failure to report.
   */
  const getRequestHistory = useCallback((): Promise<RequestHistorySummary[]> => {
    const running = historyInFlightRef.current;
    if (running) return running.then((list) => list ?? []);

    if (!sessionId || !sessionToken) {
      if (mountedRef.current) {
        setRequestHistory([]);
        setRequestHistoryStatus("unavailable");
      }
      return Promise.resolve([]);
    }

    if (mountedRef.current) setRequestHistoryStatus("loading");

    const requestId = crypto.randomUUID();
    slog.info("request history: reading", { sessionId, requestId });

    return runBridgeRead<RequestHistorySummary[]>({
      wsUrl: buildCdpWebSocketUrl(sessionId, sessionToken),
      requestId,
      tool: "cdp_get_request_history",
      args: {},
      timeoutMs: LAST_REQUEST_TIMEOUT_MS,
      label: "request history",
      sessionId,
      interpret: (raw, id) => {
        const outcome = interpretHistoryList(raw, id);
        return outcome.kind === "ok" ? { kind: "ok", value: outcome.history } : outcome;
      },
      guardRef: historyInFlightRef,
      cleanupRef: historyCleanupRef,
      onSettle: (value) => {
        if (!mountedRef.current) return;
        // Only a count is logged. The entries are URLs.
        slog.info("request history: read", {
          sessionId,
          requestId,
          count: value ? value.length : 0,
        });
        setRequestHistory(value ?? []);
        setRequestHistoryStatus(value ? "available" : "unavailable");
      },
    })
      // The list order comes from the bridge (newest first); the hook does not
      // re-sort it, so a bridge-side ordering bug stays visible instead of being
      // masked here.
      .then((list) => list ?? []);
  }, [sessionId, sessionToken]);

  /**
   * Read one history entry in full. An evicted or unknown id resolves to `null`
   * — a neutral state, not an error: the panel simply leaves the form alone.
   * This never touches `lastRequest`, `requestHistory` or the shell response.
   */
  const getRequestHistoryEntry = useCallback(
    (id: number): Promise<RequestHistoryEntry | null> => {
      const running = entryInFlightRef.current;
      // Same entry twice: one socket. Different entry: the older read is
      // superseded, so a slow first answer cannot overwrite the newer choice.
      if (running && entryInFlightIdRef.current === id) return running;
      if (running) {
        entryCleanupRef.current?.();
        entryCleanupRef.current = null;
      }

      if (!sessionId || !sessionToken) return Promise.resolve(null);

      const requestId = crypto.randomUUID();
      entryInFlightIdRef.current = id;
      slog.info("request history: reading entry", { sessionId, requestId });

      return runBridgeRead<RequestHistoryEntry>({
        wsUrl: buildCdpWebSocketUrl(sessionId, sessionToken),
        requestId,
        tool: "cdp_get_request_history",
        // The id of the entry, not of the tab: the two are distinct, and the
        // answer now carries `tab_id` as well (214).
        args: { id },
        timeoutMs: LAST_REQUEST_TIMEOUT_MS,
        label: "request history entry",
        sessionId,
        interpret: (raw, rid) => {
          const outcome = interpretHistoryEntry(raw, rid);
          return outcome.kind === "ok" ? { kind: "ok", value: outcome.request } : outcome;
        },
        guardRef: entryInFlightRef,
        cleanupRef: entryCleanupRef,
        onSettle: (value) => {
          if (entryInFlightIdRef.current === id) entryInFlightIdRef.current = null;
          // Neither URL, headers nor body is logged.
          slog.info("request history: entry read", {
            sessionId,
            requestId,
            found: value !== null,
          });
        },
      });
    },
    [sessionId, sessionToken],
  );

  /**
   * Task 215 — read the currently open tabs once, on demand.
   *
   * Never rejects, never polls. No open tab is `available` with an empty list,
   * not `unavailable`: nothing to browse is an answer, not a failure.
   */
  const getOpenTabs = useCallback((): Promise<OpenTab[]> => {
    const running = tabsInFlightRef.current;
    if (running) return running.then((list) => list ?? []);

    if (!sessionId || !sessionToken) {
      if (mountedRef.current) {
        setOpenTabs([]);
        setOpenTabsStatus("unavailable");
      }
      return Promise.resolve([]);
    }

    if (mountedRef.current) setOpenTabsStatus("loading");

    const requestId = crypto.randomUUID();
    slog.info("open tabs: reading", { sessionId, requestId });

    return runBridgeRead<OpenTab[]>({
      wsUrl: buildCdpWebSocketUrl(sessionId, sessionToken),
      requestId,
      // The bridge command that already exists; 215 adds no bridge surface.
      tool: "cdp_list_tabs",
      args: {},
      timeoutMs: LAST_REQUEST_TIMEOUT_MS,
      label: "open tabs",
      sessionId,
      interpret: (raw, id) => {
        const outcome = interpretOpenTabs(raw, id);
        return outcome.kind === "ok" ? { kind: "ok", value: outcome.tabs } : outcome;
      },
      guardRef: tabsInFlightRef,
      cleanupRef: tabsCleanupRef,
      onSettle: (value) => {
        if (!mountedRef.current) return;
        // A count only. The entries are URLs and titles.
        slog.info("open tabs: read", {
          sessionId,
          requestId,
          count: value ? value.length : 0,
        });
        setOpenTabs(value ?? []);
        setOpenTabsStatus(value ? "available" : "unavailable");
      },
    }).then((list) => list ?? []);
  }, [sessionId, sessionToken]);

  /**
   * Task 219 — read the URL and title of the tab Chrome really shows.
   *
   * The same `cdp_evaluate_active_tab` as `send()` (209), so the answer comes
   * from the visible tab and not from the `active` flag of `list_tabs`, which
   * the bridge publishes as `false` for every tab. Never rejects, never polls,
   * never logs the URL or the title.
   */
  const getActivePage = useCallback((): Promise<ActivePage | null> => {
    const running = activePageInFlightRef.current;
    if (running) return running;
    if (!sessionId || !sessionToken) return Promise.resolve(null);

    const requestId = crypto.randomUUID();
    slog.info("active page: reading", { sessionId, requestId });

    return runBridgeRead<ActivePage>({
      wsUrl: buildCdpWebSocketUrl(sessionId, sessionToken),
      requestId,
      tool: "cdp_evaluate_active_tab",
      // The same argument shape as `send()`: both bridge variants accept it.
      args: { expression: ACTIVE_PAGE_EXPRESSION, awaitPromise: true, returnByValue: true },
      timeoutMs: LAST_REQUEST_TIMEOUT_MS,
      label: "active page",
      sessionId,
      interpret: (raw, rid) => {
        const outcome = interpretActivePage(raw, rid);
        return outcome.kind === "ok" ? { kind: "ok", value: outcome.page } : outcome;
      },
      guardRef: activePageInFlightRef,
      cleanupRef: activePageCleanupRef,
      onSettle: (value) => {
        // Found or not — never the URL, never the title.
        slog.info("active page: read", { sessionId, requestId, found: value !== null });
      },
    });
  }, [sessionId, sessionToken]);

  /**
   * Task 219 — the `Cookie` header for one URL, from cookies really read back.
   *
   * Only complete http(s) URLs are looked up. `domain` is also sent for older
   * bridges that ignore `url`, keeping their reads scoped. `""` is a successful
   * read with nothing to send; `null` is a failure or an unsupported URL. Never
   * rejects; never logs a name, a value, or the URL — a count at most.
   */
  const getCookieHeader = useCallback(
    (url: string): Promise<string | null> => {
      if (!isHttpUrl(url)) return Promise.resolve(null);
      const hostname = new URL(url).hostname;
      if (!hostname) return Promise.resolve(null);

      const running = cookiesInFlightRef.current;
      if (running && cookiesInFlightUrlRef.current === url) return running;
      if (running) {
        cookiesCleanupRef.current?.();
        cookiesCleanupRef.current = null;
      }

      if (!sessionId || !sessionToken) return Promise.resolve(null);

      const requestId = crypto.randomUUID();
      cookiesInFlightUrlRef.current = url;
      slog.info("cookies: reading", { sessionId, requestId });

      return runBridgeRead<string>({
        wsUrl: buildCdpWebSocketUrl(sessionId, sessionToken),
        requestId,
        tool: "cdp_get_cookies",
        args: { url, domain: hostname },
        timeoutMs: LAST_REQUEST_TIMEOUT_MS,
        label: "cookies",
        sessionId,
        interpret: (raw, rid) => {
          const outcome = interpretCookies(raw, rid);
          return outcome.kind === "ok"
            ? { kind: "ok", value: buildCookieHeader(outcome.cookies, url) }
            : outcome;
        },
        guardRef: cookiesInFlightRef,
        cleanupRef: cookiesCleanupRef,
        onSettle: (value) => {
          if (cookiesInFlightUrlRef.current === url) cookiesInFlightUrlRef.current = null;
          // A count of pairs joined, never a name or a value.
          slog.info("cookies: read", {
            sessionId,
            requestId,
            found: value !== null,
            count: value ? value.split("; ").length : 0,
          });
        },
      });
    },
    [sessionId, sessionToken],
  );

  /**
   * Guest mode: the backend owns cookies, signing and the guest call. The
   * session token stays out of this path entirely — it belongs to the in-page
   * WebSocket only.
   */
  const sendViaGuest = useCallback(
    async (request: ShellRequest): Promise<void> => {
      inFlightRef.current = true;
      setLoading(true);
      const startedAt = performance.now();
      const controller = new AbortController();
      cleanupRef.current = () => controller.abort();

      slog.info("shell request: guest relay", {
        sessionId,
        method: request.method,
      });

      try {
        const httpResponse = await apiFetch(`/sessions/${sessionId}/shell-request`, {
          method: "POST",
          body: JSON.stringify(toRelayDto(request)),
          signal: controller.signal,
        });
        const durationMs = Math.round(performance.now() - startedAt);
        const payload: unknown = await httpResponse.json().catch(() => null);

        if (!mountedRef.current) return;
        if (!httpResponse.ok) {
          const message = guestErrorMessage(httpResponse.status, payload);
          slog.error("shell request: guest relay refused", {
            sessionId,
            status: httpResponse.status,
          });
          setTransportError(`Relais VM : ${message}`);
          return;
        }
        const parsed = parseGuestResponse(payload, durationMs);
        if (!parsed) {
          setTransportError("Relais VM : réponse backend mal formée.");
          return;
        }
        slog.info("shell request: guest response", {
          sessionId,
          status: parsed.status,
          ms: parsed.durationMs,
        });
        setResponse(parsed);
      } catch (error) {
        if (!mountedRef.current) return;
        const aborted = error instanceof DOMException && error.name === "AbortError";
        if (!aborted) {
          slog.error("shell request: guest relay unreachable", {
            sessionId,
            error: error instanceof Error ? error.message : String(error),
          });
          setTransportError("Relais VM : backend injoignable.");
        }
      } finally {
        inFlightRef.current = false;
        cleanupRef.current = null;
        if (mountedRef.current) setLoading(false);
      }
    },
    [sessionId],
  );

  const send = useCallback(
    (request: ShellRequest, mode: ShellRequestMode = "in_page", tabId?: string,
      capture?: ShellCaptureBinding): Promise<void> => {
      if (inFlightRef.current) return Promise.resolve();

      setResponse(null);
      setPageError(null);
      setTransportError(null);
      if (!tabId || (capture && (capture.tabId !== tabId || capture.sessionId !== sessionId))) {
        setTransportError("Identité de l'onglet/capture indisponible : aucun envoi vers un onglet de remplacement.");
        return Promise.resolve();
      }

      if (!isHttpUrl(request.url)) {
        setPageError({
          kind: "invalid_url",
          message: "URL invalide : seuls les schémas http:// et https:// sont acceptés.",
        });
        return Promise.resolve();
      }
      if (!sessionId) {
        setTransportError("Transport CDP : session indisponible.");
        return Promise.resolve();
      }
      if (mode === "guest_proxy") {
        if (capture) {
          setTransportError("Une capture native ne peut pas être rejouée fidèlement par le proxy Python.");
          return Promise.resolve();
        }
        return sendViaGuest(request);
      }
      if (!sessionToken) {
        setTransportError("Transport CDP : session ou jeton indisponible.");
        return Promise.resolve();
      }

      let editedBody: string | null = null;
      try {
        if (capture?.edited && (request.bodyPresent ?? request.body.length > 0)) {
          const binary = toRelayBodyString(request);
          if (binary.length > 1024 * 1024) throw new Error("body_limit");
          editedBody = btoa(binary);
        }
      } catch {
        setPageError({kind:"page",message:"Corps invalide ou supérieur à 1 MiB."});
        return Promise.resolve();
      }
      inFlightRef.current = true;
      setLoading(true);

      const startedAt = performance.now();
      const requestId = crypto.randomUUID();
      const expression = buildFetchExpression(request, PAGE_TIMEOUT_MS);
      const nativeArgs: Record<string, unknown> | null = capture ? {
        epoch:capture.epoch,tab_id:tabId,id:capture.id,revision:capture.revision,
        ...(capture.edited ? {request:{method:request.method,url:request.url,
          headers:Object.fromEntries(request.headers.filter(h=>h.key).map(h=>[h.key,h.value])),
          body_state:(request.bodyPresent ?? request.body.length > 0) ? "complete" : "absent",
          body_base64:editedBody}} : {}),
      } : null;
      // Never log this URL: it carries the session token in its query string.
      const wsUrl = buildCdpWebSocketUrl(sessionId, sessionToken);

      slog.info("shell request: opening CDP WS", {
        sessionId,
        requestId,
        method: request.method,
      });

      return new Promise<void>((resolve) => {
        let settled = false;
        let timer: ReturnType<typeof setTimeout> | null = null;
        let socket: WebSocket | null = null;

        const teardown = () => {
          if (timer !== null) {
            clearTimeout(timer);
            timer = null;
          }
          if (socket) {
            socket.onopen = null;
            socket.onmessage = null;
            socket.onerror = null;
            socket.onclose = null;
            try {
              socket.close();
            } catch {
              // socket already closed by the browser
            }
            socket = null;
          }
          inFlightRef.current = false;
          cleanupRef.current = null;
        };

        const settle = (apply: () => void) => {
          if (settled) return;
          settled = true;
          teardown();
          if (mountedRef.current) {
            setLoading(false);
            apply();
          }
          resolve();
        };

        // Unmount: release everything without touching React state.
        cleanupRef.current = () => {
          if (settled) return;
          settled = true;
          teardown();
          resolve();
        };

        try {
          socket = new WebSocket(wsUrl);
        } catch {
          slog.error("shell request: WS constructor failed", {
            sessionId,
          });
          settle(() => setTransportError("Transport CDP : ouverture WebSocket impossible."));
          return;
        }

        timer = setTimeout(() => {
          slog.warn("shell request: transport timeout", { sessionId, requestId });
          settle(() =>
            setTransportError(
              `Transport CDP : aucune réponse du relais après ${TRANSPORT_TIMEOUT_MS} ms.`,
            ),
          );
        }, TRANSPORT_TIMEOUT_MS);

        socket.onopen = () => {
          socket?.send(
            JSON.stringify({
              action: "cdp",
              request_id: requestId,
              // Task 209: the in-page fetch must leave from the tab the user
              // is looking at, not from whichever target the bridge latched
              // onto at connect time.
              tool: nativeArgs ? "cdp_shell_execute" : "cdp_evaluate_tab",
              args: nativeArgs ?? { expression, tab_id:tabId, awaitPromise: true, returnByValue: true },
              ts: new Date().toISOString(),
            }),
          );
        };

        socket.onmessage = (event: MessageEvent) => {
          const outcome = interpretCdpMessage(event.data, requestId);
          if (outcome.kind === "ignore") return;
          const durationMs = Math.round(performance.now() - startedAt);

          if (outcome.kind === "transport") {
            slog.error("shell request: transport failure", { sessionId, requestId });
            settle(() => setTransportError(outcome.message));
            return;
          }
          if (outcome.kind === "page") {
            slog.warn("shell request: page failure", {
              sessionId,
              requestId,
              kind: outcome.error.kind,
              ms: durationMs,
            });
            settle(() => setPageError(outcome.error));
            return;
          }
          if (capture && (outcome.response.sourceTabId !== tabId || outcome.response.sourceCaptureId !== capture.id)) {
            settle(() => setTransportError("capture_response_identity_mismatch"));
            return;
          }
          slog.info("shell request: response", {
            sessionId,
            requestId,
            status: outcome.response.status,
            ms: durationMs,
          });
          settle(() => setResponse({ ...outcome.response, durationMs,
            sourceTabId: tabId, sourceCaptureId: capture?.id }));
        };

        socket.onerror = () => {
          slog.error("shell request: WS error", { sessionId, requestId });
          settle(() => setTransportError("Transport CDP : erreur de la WebSocket."));
        };

        socket.onclose = () => {
          slog.warn("shell request: WS closed before response", { sessionId, requestId });
          settle(() => setTransportError("Transport CDP : connexion fermée avant la réponse."));
        };
      });
    },
    [sessionId, sessionToken, sendViaGuest],
  );

  return {
    loading,
    response,
    pageError,
    transportError,
    send,
    lastRequest,
    lastRequestStatus,
    getLastRequest,
    requestHistory,
    requestHistoryStatus,
    getRequestHistory,
    getRequestHistoryEntry: (id) => snapshotRef.current ? getBoundCapture(id) : getRequestHistoryEntry(id),
    openTabs,
    openTabsStatus,
    getOpenTabs,
    getActivePage,
    getCookieHeader,
    snapshot,
    getSnapshot,
  };
}
